import React, { useState } from 'react';
import { css } from '@linaria/core';
import PageInfoRealisticCount from './PageInfoRealisticCount';
import { ResultsPerPage, Sorting } from '@elastic/react-search-ui';
import { components } from 'react-select';
import { Tooltip } from 'react-tooltip';
import Button from './Button';
import DownloadIcon from '../icons/Download';
import GridIcon from '../icons/Grid';
import ListTaskIcon from '../icons/ListTask';
import MapIcon from '../icons/Map';
import FormSelect from './FormSelect';
import FormLabel from './FormLabel';
import FormField from './FormField';
import { useMediaQuery } from 'react-responsive';
import ReactGA from 'react-ga4';
import { getSearchType } from '../utils/dataLayer';
import AppliedFilters from './AppliedFilters';
import { useLayoutMode } from './Layout';
import classNames from 'classnames';

const wrapper = css`
  @media (min-width: 1080px) {
    padding-bottom: 20px;
    border-bottom: solid 1px #e5dbd1;
  }
`;
const results = css`
  display: flex;
  gap: 20px;
  justify-content: space-between;
  margin-bottom: 20px;
  flex-direction: column;

  @media (min-width: 800px) {
    gap: 40px;
    align-items: center;
    flex-direction: row;
  }
`;
const options = css`
  display: flex;
  gap: 20px;

  @media (min-width: 800px) {
    align-items: center;
    gap: 25px;
  }

  @media (min-width: 800px) and (max-width: 1079px) {
    margin-bottom: 20px;
  }

  ${FormField} {
    margin-bottom: 0;
    display: flex;
    align-items: center;

    @media (min-width: 800px) {
      display: flex;
      align-items: center;
      width: auto;
    }
  }

  ${FormLabel} {
    margin-bottom: 0;
    flex-shrink: 0;
    margin-right: 20px;

    @media (min-width: 800px) {
      margin-right: 10px;
    }
  }
`;

const downloadButton = css`
  width: 100%;
  padding: 5.5px 15px;

  @media (min-width: 800px) {
    width: auto;
  }
`;

const downloadButtonWrapper = css`
  @media (max-width: 800px) {
    display: none;
  }
`;

const downloadButtonTooltip = css`
  background: #1a1a1a !important;
  border-radius: 5px !important;
  font-size: 16px !important;
`;

const downloadButtonContainer = css`
  position: relative;
`;

const viewButtonGroup = css`
  display: flex;
  gap: 10px;
  border: solid 1px #e5dbd1;
  border-radius: 5px;
  padding: 5px;
  min-width: fit-content;
  width: 100%;
  margin-bottom: 20px;
`;

const viewButton = css`
  padding: 8px 16px;
  border: none;
  border-radius: 3px;
  background-color: #fff;
  height: 60px;
  flex: 1;
  font-size: 18px;
  font-weight: 600;
  line-height: 30px;

  & svg {
    height: 20px;
    margin-right: 15px;
    margin-top: 5px;

    @media (max-width: 799px) {
      display: none;
    }
  }

  &:hover {
    background-color: #f2ede8;
  }

  &:focus {
    background-color: #f2ede8;
    color: #000;
  }

  @media (max-width: 799px) {
    height: 40px;
  }

  @media (min-width: 800px) {
    font-size: 22px;
    line-height: 34px;
  }
`;

const viewButtonActive = css`
  background-color: #004258;
  color: #fff;
  font-weight: 700;

  &:hover {
    background-color: #004258;
    color: #fff;
  }

  &:focus {
    background-color: #004258;
    color: #fff;
  }
`;

const sortByLabel = css`
  @media (max-width: 799px) {
    width: 100%;
  }
`;

const sortByField = css`
  max-width: 100%;

  @media (max-width: 799px) {
    width: 100%;
  }

  @media (min-width: 800px) {
    min-width: 210px;
  }
`;

const SortOption = props => {
  props.innerProps['data-transaction-name'] = `sorting`;
  return <components.Option {...props}>{props.data.label}</components.Option>;
};

const ResultsOption = props => {
  props.innerProps['data-transaction-name'] = `results per page`;
  return <components.Option {...props}>{props.data.label}</components.Option>;
};

export default function ResultHeader({
  connector,
  configHelper,
  filters,
  wasSearched,
  totalResults,
  hideViewButtons,
  hideDownload,
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const isMobile = useMediaQuery({ query: '(max-width: 799px)' });
  const isTablet = useMediaQuery({
    query: '(min-width: 800px) and (max-width: 1079px)',
  });
  const isTabletUp = useMediaQuery({ query: '(min-width: 800px)' });
  const canDownload = totalResults > 0 && totalResults < connector.maxResults();
  const { layoutMode, setLayoutMode, availableLayouts } = useLayoutMode();
  const searchType = getSearchType();

  const handleLayoutModeChange = newLayoutMode => {
    const prevLayoutMode = layoutMode;

    ReactGA.event('search_view_selected', {
      layout_mode: newLayoutMode,
      search_type: searchType,
      previous_layout_mode: prevLayoutMode,
    });

    setLayoutMode(newLayoutMode);
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    await connector.onDownloadResults();
    setTimeout(() => {
      setIsDownloading(false);
    }, 5000);
  };

  return (
    <div className={classNames(layoutMode !== 'map' && wrapper)}>
      <div className={results}>
        <div>{wasSearched && <PageInfoRealisticCount />}</div>
        {isMobile && filters.length > 0 && (
          <div>
            <AppliedFilters />
          </div>
        )}
        {!hideDownload && (
          <div className={downloadButtonWrapper}>
            <div className={downloadButtonContainer}>
              <Button
                StartIcon={DownloadIcon}
                className={downloadButton}
                data-tooltip-id="download-button-tooltip"
                disabled={!canDownload || isDownloading}
                onClick={handleDownload}
                size="small"
                variant="neutral"
              >
                Download
              </Button>
            </div>

            {!canDownload && (
              <Tooltip
                className={downloadButtonTooltip}
                id="download-button-tooltip"
              >
                Download search results (CSV) is only available for
                <br />
                queries with under 10,000 results
              </Tooltip>
            )}

            {isDownloading && (
              <Tooltip
                className={downloadButtonTooltip}
                id="download-button-tooltip"
                isOpen={true}
              >
                Downloading...
              </Tooltip>
            )}
          </div>
        )}
      </div>

      {wasSearched && availableLayouts.length > 1 && !hideViewButtons && (
        <div className={viewButtonGroup}>
          <Button
            StartIcon={ListTaskIcon}
            className={classNames(
              viewButton,
              layoutMode === 'list' && viewButtonActive,
            )}
            onClick={() => handleLayoutModeChange('list')}
          >
            <span>{`List${isTabletUp ? ' view' : ''}`}</span>
          </Button>
          {availableLayouts.includes('grid') && (
            <Button
              StartIcon={GridIcon}
              className={classNames(
                viewButton,
                layoutMode === 'grid' && viewButtonActive,
              )}
              onClick={() => handleLayoutModeChange('grid')}
            >
              <span>{`Grid${isTabletUp ? ' view' : ''}`}</span>
            </Button>
          )}
          {availableLayouts.includes('map') && (
            <Button
              StartIcon={MapIcon}
              className={classNames(
                viewButton,
                layoutMode === 'map' && viewButtonActive,
              )}
              onClick={() => handleLayoutModeChange('map')}
            >
              <span>{`Map${isTabletUp ? ' view' : ''}`}</span>
            </Button>
          )}
        </div>
      )}

      {layoutMode !== 'map' && (
        <div className={options}>
          {wasSearched && !isTablet && !isMobile && (
            <ResultsPerPage
              options={configHelper.getPageSizeOptions()}
              view={({ onChange, options, value }) => (
                <FormSelect
                  components={{
                    Option: ResultsOption,
                  }}
                  id="results-per-page"
                  isClearable={false}
                  isSearchable={false}
                  label="Show"
                  onChange={o => onChange(o.value)}
                  options={options.map(option => ({
                    label: option,
                    value: option,
                  }))}
                  value={value}
                />
              )}
            />
          )}

          {wasSearched && (
            <>
              <div className={sortByLabel}>
                <Sorting
                  sortOptions={configHelper.buildSortOptionsFromConfig()}
                  view={({ onChange, options, value }) => (
                    <FormSelect
                      className={sortByField}
                      components={{
                        Option: SortOption,
                      }}
                      id="sort"
                      isClearable={false}
                      isSearchable={false}
                      label={'Sort by'}
                      onChange={o => onChange(o.value)}
                      options={options}
                      value={value}
                    />
                  )}
                />
              </div>
            </>
          )}
        </div>
      )}

      {isTablet && filters.length > 0 && (
        <div>
          <AppliedFilters />
        </div>
      )}
    </div>
  );
}
